import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';
import { C, alpha } from '../lib/theme.js';
import { Button, Card, ErrorLine, PageHeader, StatusPill } from '../ui/ui.js';
import type { AuditEntry, AuditPage, SyncRun, SyncRuns } from '../lib/types.js';

function fmt(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

// A run still marked running/queued long after it started never reported back —
// treat it as interrupted so the row doesn't spin forever.
const STALE_MS = 30 * 60 * 1000; // 30 minutes
function isStale(r: SyncRun): boolean {
  const s = r.status.toLowerCase();
  const openState = s === 'running' || s === 'queued' || s === 'pending';
  if (!openState || r.finished_at) return false;
  return Date.now() - new Date(r.started_at).getTime() > STALE_MS;
}

/** Color a dot by the action verb. */
function actionDot(action: string): string {
  if (action.includes('create') || action.includes('seed')) return C.good;
  if (action.includes('delete') || action.includes('error')) return C.act;
  if (action.includes('update') || action.includes('budget')) return C.watch;
  return C.info;
}

export function Activity() {
  const { orgId } = useOrg();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [runs, setRuns] = useState<SyncRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openRun, setOpenRun] = useState<string | null>(null);

  const loadAudit = useCallback(
    async (before?: string | null) => {
      const qs = before ? `?before=${encodeURIComponent(before)}` : '';
      const page = await apiFetch<AuditPage>(`/api/orgs/${orgId}/audit${qs}`);
      setEntries((prev) => (before ? [...prev, ...page.entries] : page.entries));
      setHasMore(page.has_more);
      setNextBefore(page.next_before);
    },
    [orgId],
  );

  useEffect(() => {
    if (!orgId) return;
    setEntries([]);
    setRuns(null);
    loadAudit().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'Failed to load audit log'),
    );
    apiFetch<SyncRuns>(`/api/orgs/${orgId}/sync-runs`)
      .then((d) => setRuns(d.runs))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load sync runs'));
  }, [orgId, loadAudit]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      await loadAudit(nextBefore);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div style={{ padding: '26px 30px 40px', maxWidth: 1000, animation: 'basilfade .3s ease' }}>
      <PageHeader eyebrow="Audit log · sync runs" title="Activity" />
      {error && <ErrorLine>{error}</ErrorLine>}

      {/* background jobs */}
      <div className="label-mono" style={{ marginBottom: 12 }}>
        Background jobs
      </div>
      <Card style={{ overflow: 'hidden', marginBottom: 30 }}>
        {runs === null ? (
          <div
            style={{
              padding: '16px 20px',
              color: C.muted,
              font: `500 12px 'IBM Plex Mono',monospace`,
            }}
          >
            LOADING…
          </div>
        ) : runs.length === 0 ? (
          <div style={{ padding: '16px 20px', color: C.faint, fontSize: 13 }}>
            No sync runs yet — background jobs (Meta insights, Cloudbeds, CAPI) populate this in
            Phase 1+.
          </div>
        ) : (
          runs.map((r: SyncRun) => {
            const stale = isStale(r);
            const status = stale ? 'error' : r.status;
            const label = stale ? 'INTERRUPTED' : undefined;
            const expandable = !!r.error || stale;
            const isOpen = openRun === r.id;
            return (
              <div key={r.id} style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
                <div
                  onClick={() => expandable && setOpenRun(isOpen ? null : r.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '13px 18px',
                    cursor: expandable ? 'pointer' : 'default',
                  }}
                >
                  <span
                    style={{
                      color: C.faint,
                      width: 10,
                      flexShrink: 0,
                      transition: 'transform .15s',
                      transform: isOpen ? 'rotate(90deg)' : 'none',
                      opacity: expandable ? 1 : 0,
                    }}
                  >
                    ›
                  </span>
                  <code
                    style={{
                      font: `500 12px 'IBM Plex Mono',monospace`,
                      color: C.secondary,
                      width: 150,
                    }}
                  >
                    {r.kind}
                  </code>
                  <StatusPill status={status} label={label} />
                  <span style={{ font: `500 11px 'IBM Plex Mono',monospace`, color: C.faint }}>
                    {r.trigger}
                  </span>
                  {expandable && !isOpen && (
                    <span style={{ font: `500 11px 'IBM Plex Mono',monospace`, color: C.act }}>
                      {stale ? 'never finished — click for details' : 'error — click to see why'}
                    </span>
                  )}
                  <span
                    style={{
                      marginLeft: 'auto',
                      font: `500 11px 'IBM Plex Mono',monospace`,
                      color: C.faint,
                    }}
                  >
                    {fmt(r.started_at)}
                  </span>
                </div>
                {isOpen && (
                  <div
                    style={{
                      padding: '0 18px 14px 44px',
                      fontSize: 12.5,
                      color: C.secondary,
                      lineHeight: 1.6,
                    }}
                  >
                    {r.error && (
                      <div
                        style={{
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: alpha(C.act, 8),
                          border: `1px solid ${alpha(C.act, 25)}`,
                          color: C.primary,
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                        }}
                      >
                        {r.error}
                      </div>
                    )}
                    {stale && !r.error && (
                      <div style={{ color: C.muted }}>
                        This job started {fmt(r.started_at)} and never reported finishing — it was
                        likely interrupted. A fresh run will supersede it.
                      </div>
                    )}
                    <div style={{ color: C.faint, marginTop: 8 }}>
                      Started {fmt(r.started_at)} · Finished {fmt(r.finished_at)}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </Card>

      {/* audit timeline */}
      <div className="label-mono" style={{ marginBottom: 12 }}>
        Audit log
      </div>
      <Card style={{ overflow: 'hidden' }}>
        {entries.length === 0 ? (
          <div style={{ padding: '16px 20px', color: C.faint, fontSize: 13 }}>
            No audit entries yet.
          </div>
        ) : (
          entries.map((e: AuditEntry) => (
            <div
              key={e.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '14px 20px',
                borderBottom: `1px solid ${C.lineSoft}`,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: actionDot(e.action),
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  font: `500 11px 'IBM Plex Mono',monospace`,
                  color: C.faint,
                  width: 150,
                  flexShrink: 0,
                }}
              >
                {fmt(e.created_at)}
              </span>
              <span style={{ flex: 1, fontSize: 13, color: C.primary }}>
                <code style={{ font: `500 12px 'IBM Plex Mono',monospace`, color: C.secondary }}>
                  {e.action}
                </code>
                <span style={{ color: C.faint }}>
                  {' '}
                  · {e.entity_type}
                  {e.entity_id ? ` · ${e.entity_id.slice(0, 8)}` : ''}
                </span>
              </span>
              <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.muted }}>
                {e.actor_name ?? (e.actor_user_id ? 'user' : 'system')}
              </span>
            </div>
          ))
        )}
      </Card>
      {hasMore && (
        <Button onClick={() => void loadMore()} disabled={loadingMore} style={{ marginTop: 12 }}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}
